import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { uiApi } from '@/api/ui.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { queryKeys, tierOptions } from '@/lib/query';
import type { NavItem, NavWorkflowGroup } from '@/components/layout/Sidebar';
import type { UiMenuGroup } from '@/types/ui';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { filterHiddenNavItems, SIDEBAR_HIDDEN_GROUP_IDS } from '@/config/navHiddenRoutes';
import { isSettingsLegacyRoute } from '@/config/settingsRegistry';
import { pageNavIcon } from '@/utils/pageNavIcon';

/**
 * Converts backend menu manifest into sidebar NavWorkflowGroup[].
 * Session cache first; fetches GET /v1/ui/menus when session nav is empty.
 */
export function useBackendNavigation(module?: string | null): NavWorkflowGroup[] {
  const sessionNav = useAppSelector((s) => s.session.navigation);

  const { data: fetchedNav } = useQuery({
    queryKey: queryKeys.ui.menus(module),
    queryFn: () => uiApi.menus(module ?? undefined),
    enabled: !sessionNav?.length,
    ...tierOptions('master'),
  });

  const navigation = sessionNav?.length ? sessionNav : (fetchedNav ?? []);

  return useMemo(() => {
    if (!navigation?.length) return [];
    const groups = module
      ? navigation.filter((g) => !g.module || g.module === module)
      : navigation;
    return groups
      .filter((group) => !SIDEBAR_HIDDEN_GROUP_IDS.has(group.id))
      .map((group) => backendGroupToNavGroup(group, module))
      .filter((group) => group.items.length > 0);
  }, [navigation, module]);
}

function backendGroupToNavGroup(group: UiMenuGroup, module?: string | null): NavWorkflowGroup {
  const items: NavItem[] = filterHiddenNavItems(group.items ?? [])
    .filter((item) => {
      const path = item.route.split('?')[0];
      if (isSettingsLegacyRoute(path)) return false;
      if (!module) return true;
      if (isPlatformAdminRoute(path)) return true;
      const pathModule = resolveModuleFromPath(path);
      return !pathModule || pathModule === module;
    })
    .map((item) => ({
      to: item.route,
      label: item.label,
      labelKey: item.labelKey,
      icon: pageNavIcon(item.icon, item.route),
      feature: item.feature ?? undefined,
    }));
  return {
    id: group.id,
    label: group.label,
    labelKey: group.labelKey,
    pinned: group.pinned,
    items,
  };
}
